import { ReactNode, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { PostMessageCodes } from '../../../Constants/PostMessageCodes';
import { postMessageToParentApp } from '../../../utils/Utilities';

/**
 * Enhanced ScrollMessageWrapper that waits for iframe height to stabilize.
 *
 * Root cause: The review screen's height ramps from ~2000px to ~3900-3993px during
 * accordion animations. If scroll-to-top fires while height is still changing, MyAccount
 * scrolls to the top of a short iframe, then the iframe expands and pushes content under
 * the 205px fixed header, causing a cutoff.
 *
 * Solution (Option B - Height Stabilization): Use a ResizeObserver on the app wrapper
 * element (the same element iframe-resizer measures) to react to height changes as they
 * happen, and delay DIGITAL_CLAIM_V2_SCROLL until the height hasn't changed for
 * SETTLE_THRESHOLD ms. Unlike polling on an interval, the observer callback fires exactly
 * when the browser finishes laying out a resize, so the settle window starts immediately
 * rather than up to a poll-interval late.
 *
 * A MAX_WAIT_TIMEOUT safety fallback still forces the scroll message if the height never
 * settles (e.g. continuous animation) so the user isn't left without a scroll.
 *
 * Reference: review-screen-top-cutoff-fixed-header-overlap.md
 * Issue: Intermittent cutoff (~1 in 4-5) on Windows VDI (1920x1080, 150% scale, ~551px viewport)
 */

// How long to wait after height stabilizes before considering layout "settled"
const SETTLE_THRESHOLD = 300; // ms

// Maximum time to wait before forcing scroll (safety fallback)
const MAX_WAIT_TIMEOUT = 2000; // ms

export function ScrollMessageWrapperHeightStabilized({ children }: { children: ReactNode }): JSX.Element {
  const location = useLocation();
  const settleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const forceScrollTimerRef = useRef<NodeJS.Timeout | null>(null);
  const scrollMessageSentRef = useRef<boolean>(false);

  useEffect(() => {
    // Reset the flag when the route changes
    scrollMessageSentRef.current = false;

    const sendScrollMessage = () => {
      // Only send the scroll message once per route
      if (scrollMessageSentRef.current) {
        return;
      }

      scrollMessageSentRef.current = true;
      postMessageToParentApp(PostMessageCodes.DIGITAL_CLAIM_V2_SCROLL);

      if (forceScrollTimerRef.current) {
        clearTimeout(forceScrollTimerRef.current);
        forceScrollTimerRef.current = null;
      }
    };

    // Safety fallback: force scroll after MAX_WAIT_TIMEOUT even if height never settles
    forceScrollTimerRef.current = setTimeout(sendScrollMessage, MAX_WAIT_TIMEOUT);

    // Get the app wrapper element that iframe-resizer measures
    const appWrapper = document.querySelector('[data-iframe-height]');

    let resizeObserver: ResizeObserver | null = null;
    if (appWrapper) {
      resizeObserver = new ResizeObserver(() => {
        // Height changed (or observation just started): reset the settle timer
        if (settleTimerRef.current) {
          clearTimeout(settleTimerRef.current);
        }
        settleTimerRef.current = setTimeout(sendScrollMessage, SETTLE_THRESHOLD);
      });
      resizeObserver.observe(appWrapper);
    }

    // Cleanup on unmount or route change
    return () => {
      if (settleTimerRef.current) {
        clearTimeout(settleTimerRef.current);
        settleTimerRef.current = null;
      }
      if (forceScrollTimerRef.current) {
        clearTimeout(forceScrollTimerRef.current);
        forceScrollTimerRef.current = null;
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [location.pathname]);

  return <div>{children}</div>;
}
